<template>
    <section class="two-factor-page">
        <section></section>
        <section class="two-factor-page__wrapper">
            <eSesjaLogo class="two-factor-page__logo" />
            <section class="two-factor-card">
                <template v-if="loading">
                    <h1 class="two-factor-card__header">
                        {{ $t('com.file.login.two-factor-title') }}
                    </h1>
                    <p class="two-factor-card__text">
                        {{ $t('com.file.login.two-factor-loading') }}
                    </p>
                </template>

                <form v-else-if="flow === 'verify'" @submit.prevent="verifyCode">
                    <h1 class="two-factor-card__header">
                        {{ $t('com.file.login.two-factor-title') }}
                    </h1>
                    <p class="two-factor-card__expiration">
                        {{ $t('com.file.login.two-factor-expires-in', { seconds: remainingSeconds }) }}
                    </p>

                    <WarningBox v-if="errorKey">
                        {{ $t(errorKey) }}
                    </WarningBox>

                    <Card class="two-factor-card__method">
                        <CardHeader v-if="availableMethods.length > 1" class="two-factor-card__method-tabs"
                            role="tablist" :aria-label="$t('com.file.login.two-factor-title')">
                            <button v-if="availableMethods.includes('totp')" id="two-factor-method-tab-totp"
                                class="two-factor-card__method-tab"
                                :class="{ 'two-factor-card__method-tab--active': verificationMethod === 'totp' }"
                                type="button" role="tab" :aria-selected="verificationMethod === 'totp'"
                                aria-controls="two-factor-method-panel"
                                @click="selectVerificationMethod('totp')">
                                {{ $t('com.file.login.two-factor-method-totp') }}
                            </button>
                            <button v-if="availableMethods.includes('recovery_code')"
                                id="two-factor-method-tab-recovery-code" class="two-factor-card__method-tab"
                                :class="{ 'two-factor-card__method-tab--active': verificationMethod === 'recovery_code' }"
                                type="button" role="tab" :aria-selected="verificationMethod === 'recovery_code'"
                                aria-controls="two-factor-method-panel"
                                @click="selectVerificationMethod('recovery_code')">
                                {{ $t('com.file.login.two-factor-method-recovery-code') }}
                            </button>
                        </CardHeader>
                        <CardBody id="two-factor-method-panel" class="two-factor-card__method-body" role="tabpanel"
                            :aria-labelledby="`two-factor-method-tab-${verificationMethod === 'recovery_code' ? 'recovery-code' : 'totp'}`">
                            <p class="two-factor-card__method-instruction">
                                {{ $t(verificationInstructionKey) }}
                            </p>
                            <label for="two-factor-verification-code" class="two-factor-card__label">
                                {{ $t(verificationCodeLabelKey) }}
                            </label>
                            <input id="two-factor-verification-code" v-model.trim="verificationCode"
                                class="two-factor-card__input"
                                :inputmode="verificationMethod === 'totp' ? 'numeric' : 'text'"
                                autocomplete="one-time-code" :placeholder="$t(verificationCodePlaceholderKey)"
                                autofocus />
                        </CardBody>
                    </Card>

                    <button class="two-factor-card__primary-button" type="submit" :disabled="submitting || isExpired">
                        {{ $t('com.file.login.two-factor-confirm') }}
                    </button>
                    <button class="two-factor-card__secondary-button" type="button" :disabled="submitting"
                        @click="cancelChallenge">
                        {{ $t('com.file.login.two-factor-back') }}
                    </button>
                </form>

                <template v-else-if="flow === 'enrollment'">
                    <template v-if="enrollmentStage === 'setup'">
                        <form @submit.prevent="confirmEnrollment">
                            <h1 class="two-factor-card__header">
                                {{ $t('com.file.login.two-factor-enrollment-title') }}
                            </h1>
                            <p class="two-factor-card__text">
                                {{ $t('com.file.login.two-factor-enrollment-required-info') }}
                            </p>
                            <p class="two-factor-card__expiration">
                                {{ $t('com.file.login.two-factor-expires-in', { seconds: remainingSeconds }) }}
                            </p>

                            <WarningBox v-if="errorKey">
                                {{ $t(errorKey) }}
                            </WarningBox>

                            <p class="two-factor-card__text two-factor-card__text--left">
                                {{ $t('com.file.profile.two-factor-scan-info') }}
                            </p>
                            <section class="two-factor-card__enrollment">
                                <img v-if="qrCodeDataUrl" class="two-factor-card__qr" :src="qrCodeDataUrl"
                                    :alt="$t('com.file.profile.two-factor-qr-alt')" />
                                <section class="two-factor-card__manual-key">
                                    <span class="two-factor-card__label">
                                        {{ $t('com.file.profile.two-factor-manual-key') }}
                                    </span>
                                    <code>{{ enrollment.manualKey }}</code>
                                    <span v-if="enrollment.accountName">
                                        {{ $t('com.file.profile.two-factor-account') }}: {{ enrollment.accountName }}
                                    </span>
                                    <span v-if="enrollment.issuer">
                                        {{ $t('com.file.profile.two-factor-issuer') }}: {{ enrollment.issuer }}
                                    </span>
                                </section>
                            </section>

                            <label for="two-factor-enrollment-code" class="two-factor-card__label">
                                {{ $t('com.file.profile.two-factor-code') }}
                            </label>
                            <input id="two-factor-enrollment-code" v-model.trim="verificationCode"
                                class="two-factor-card__input" inputmode="numeric" autocomplete="one-time-code"
                                maxlength="6" :placeholder="$t('com.file.login.two-factor-code-placeholder')" />

                            <button class="two-factor-card__primary-button" type="submit"
                                :disabled="submitting || isExpired || !enrollment.manualKey">
                                {{ $t('com.file.profile.two-factor-activate') }}
                            </button>
                            <button class="two-factor-card__secondary-button" type="button" :disabled="submitting"
                                @click="cancelChallenge">
                                {{ $t('com.global.cancel') }}
                            </button>
                        </form>
                    </template>

                    <template v-else>
                        <h1 class="two-factor-card__header">
                            {{ $t('com.file.profile.two-factor-recovery-codes-title') }}
                        </h1>
                        <p class="two-factor-card__notice">
                            {{ $t('com.file.profile.two-factor-recovery-codes-once') }}
                        </p>
                        <WarningBox v-if="errorKey">
                            {{ $t(errorKey) }}
                        </WarningBox>
                        <p v-if="recoveryCodesUnavailable" class="two-factor-card__notice">
                            {{ $t('com.file.login.two-factor-recovery-codes-unavailable') }}
                        </p>
                        <section v-else class="two-factor-card__codes">
                            <code v-for="recoveryCode in recoveryCodes" :key="recoveryCode">
                                {{ recoveryCode }}
                            </code>
                        </section>
                        <section v-if="!recoveryCodesUnavailable" class="two-factor-card__code-actions">
                            <button type="button" class="two-factor-card__secondary-button two-factor-card__secondary-button--inline"
                                @click="copyRecoveryCodes">
                                <img class="two-factor-card__button-icon" src="@/assets/icons/components/copy.svg"
                                    alt="" aria-hidden="true" />
                                {{ $t('com.file.profile.two-factor-copy-codes') }}
                            </button>
                            <button type="button" class="two-factor-card__secondary-button two-factor-card__secondary-button--inline"
                                @click="downloadRecoveryCodes">
                                <img class="two-factor-card__button-icon"
                                    src="@/assets/icons/components/download-black.svg" alt="" aria-hidden="true" />
                                {{ $t('com.file.profile.two-factor-download-codes') }}
                            </button>
                        </section>
                        <p v-if="successKey" class="two-factor-card__success">
                            {{ $t(successKey) }}
                        </p>
                        <label class="two-factor-card__confirmation">
                            <input v-model="recoveryCodesSaved" type="checkbox" />
                            <span>{{ $t(recoveryCodesConfirmationKey) }}</span>
                        </label>
                        <button type="button" class="two-factor-card__primary-button"
                            :disabled="submitting || !recoveryCodesSaved || isExpired" @click="completeEnrollment">
                            {{ $t('com.file.profile.two-factor-finish') }}
                        </button>
                        <button class="two-factor-card__secondary-button" type="button" :disabled="submitting"
                            @click="cancelChallenge">
                            {{ $t('com.global.cancel') }}
                        </button>
                    </template>
                </template>

                <template v-else>
                    <h1 class="two-factor-card__header">
                        {{ $t('com.file.login.two-factor-title') }}
                    </h1>
                    <WarningBox>
                        {{ $t(errorKey || 'com.file.login.two-factor-state-error') }}
                    </WarningBox>
                    <button class="two-factor-card__secondary-button" type="button" @click="returnToLogin">
                        {{ $t('com.file.login.two-factor-back') }}
                    </button>
                </template>
            </section>
        </section>
        <eSesjaFooter />
    </section>
</template>

<script charset="utf-8">
import QRCode from "qrcode";
import WarningBox from "@/components/BasicControls/WarningBox.vue";
import eSesjaFooter from "@/components/Shared/eSesjaFooter.vue";
import eSesjaLogo from "@/components/Shared/eSesjaLogo.vue";
import { broadcastChannelService } from "@/services/broadcast.channel.service";
import UserService from "@/services/user.service";

export default {
    name: "TwoFactor",
    components: {
        WarningBox,
        eSesjaFooter,
        eSesjaLogo,
    },
    data() {
        return {
            loading: true,
            submitting: false,
            flow: null,
            availableMethods: [],
            verificationMethod: "totp",
            verificationCode: "",
            enrollmentStage: "setup",
            enrollment: {
                provisioningUri: "",
                manualKey: "",
                issuer: "",
                accountName: "",
            },
            qrCodeDataUrl: "",
            recoveryCodes: [],
            recoveryCodesSaved: false,
            recoveryCodesUnavailable: false,
            remainingSeconds: 0,
            timer: null,
            errorKey: null,
            successKey: null,
        };
    },
    computed: {
        isExpired() {
            return this.remainingSeconds <= 0;
        },
        verificationInstructionKey() {
            if (this.verificationMethod === "recovery_code") {
                return "com.file.login.two-factor-recovery-instruction";
            }

            return "com.file.login.two-factor-totp-instruction";
        },
        verificationCodeLabelKey() {
            if (this.verificationMethod === "recovery_code") {
                return "com.file.login.two-factor-recovery-code-label";
            }

            return "com.file.login.two-factor-code-label";
        },
        verificationCodePlaceholderKey() {
            if (this.verificationMethod === "recovery_code") {
                return "com.file.login.two-factor-recovery-code-placeholder";
            }

            return "com.file.login.two-factor-code-placeholder";
        },
        recoveryCodesConfirmationKey() {
            if (this.recoveryCodesUnavailable) {
                return "com.file.login.two-factor-recovery-codes-unavailable-confirmation";
            }

            return "com.file.profile.two-factor-codes-saved-confirmation";
        },
    },
    async mounted() {
        await this.restoreChallenge();
    },
    beforeUnmount() {
        this.clearTimer();
    },
    methods: {
        async restoreChallenge() {
            try {
                const response = await UserService.getLoginTwoFactorState();
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const state = response.data ?? {};
                this.flow = state.flow;
                this.remainingSeconds = Number(state.expiresInSeconds) || 0;
                this.startTimer();

                if (this.flow === "verify") {
                    this.availableMethods = Array.isArray(state.methods) && state.methods.length > 0
                        ? state.methods
                        : ["totp"];
                    this.verificationMethod = this.availableMethods.includes("totp")
                        ? "totp"
                        : this.availableMethods[0];
                }
                else if (this.flow === "enrollment") {
                    if (state.enrollmentConfirmed === true) {
                        this.enrollmentStage = "recovery";
                        this.recoveryCodesUnavailable = true;
                    }
                    else {
                        await this.startEnrollment();
                    }
                }
                else {
                    this.flow = null;
                    this.errorKey = "com.file.login.two-factor-state-error";
                }
            }
            catch (error) {
                const status = error.response?.status;
                if (status === 400 || status === 410 || status === 401 || status === 404) {
                    await this.returnToLogin();
                    return;
                }

                if (status === 429) {
                    this.flow = null;
                    this.errorKey = "com.file.login.two-factor-attempts-exceeded";
                    return;
                }

                this.flow = null;
                this.errorKey = "com.file.login.two-factor-state-error";
            }
            finally {
                this.loading = false;
            }
        },
        async startEnrollment() {
            try {
                const response = await UserService.startRequiredTotpEnrollment();
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const data = response.data ?? {};
                if (!data.provisioningUri || !data.manualKey) {
                    this.errorKey = "com.file.login.two-factor-configuration-error";
                    return;
                }

                this.enrollment = {
                    provisioningUri: data.provisioningUri,
                    manualKey: data.manualKey,
                    issuer: data.issuer ?? "",
                    accountName: data.accountName ?? "",
                };
                this.remainingSeconds = Number(data.expiresInSeconds) || this.remainingSeconds;
                this.qrCodeDataUrl = await QRCode.toDataURL(data.provisioningUri, {
                    errorCorrectionLevel: "M",
                    margin: 1,
                    width: 220,
                });
            }
            catch (error) {
                this.setChallengeError(error, "com.file.login.two-factor-configuration-error");
            }
        },
        async verifyCode() {
            if (!this.verificationCode) {
                this.errorKey = "com.file.login.two-factor-code-required";
                return;
            }

            if (this.isExpired) {
                this.errorKey = "com.file.login.two-factor-expired";
                return;
            }

            try {
                this.submitting = true;
                this.errorKey = null;
                await UserService.verifyTwoFactorLogin(this.verificationMethod, this.verificationCode);
                this.clearTimer();
                broadcastChannelService.notifyLogin();
            }
            catch (error) {
                this.setChallengeError(error, "com.file.login.two-factor-invalid-code");
            }
            finally {
                this.submitting = false;
            }
        },
        async confirmEnrollment() {
            if (!this.verificationCode) {
                this.errorKey = "com.file.login.two-factor-code-required";
                return;
            }

            if (this.isExpired) {
                this.errorKey = "com.file.login.two-factor-expired";
                return;
            }

            try {
                this.submitting = true;
                this.errorKey = null;
                const response = await UserService.confirmRequiredTotpEnrollment(this.verificationCode);
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const recoveryCodes = this.extractRecoveryCodes(response.data);
                if (recoveryCodes.length === 0) {
                    this.errorKey = "com.file.profile.two-factor-recovery-codes-error";
                    return;
                }

                this.verificationCode = "";
                this.recoveryCodes = recoveryCodes;
                this.recoveryCodesUnavailable = false;
                this.recoveryCodesSaved = false;
                this.enrollmentStage = "recovery";
            }
            catch (error) {
                this.setChallengeError(error, "com.file.login.two-factor-invalid-code");
            }
            finally {
                this.submitting = false;
            }
        },
        async completeEnrollment() {
            if (!this.recoveryCodesSaved || this.isExpired) {
                return;
            }

            try {
                this.submitting = true;
                this.errorKey = null;
                await UserService.completeRequiredTotpEnrollment();
                this.recoveryCodes = [];
                this.clearTimer();
                broadcastChannelService.notifyLogin();
            }
            catch (error) {
                this.setChallengeError(error, "com.file.login.two-factor-completion-error");
            }
            finally {
                this.submitting = false;
            }
        },
        async cancelChallenge() {
            try {
                this.submitting = true;
                if (this.flow === "enrollment") {
                    await UserService.cancelLoginTwoFactor();
                }
            }
            catch (error) {
                const status = error.response?.status;
                if (status === 410 || status === 401 || status === 404) {
                    await this.returnToLogin();
                    return;
                }

                this.errorKey = "com.file.login.two-factor-cancel-error";
                return;
            }
            finally {
                this.submitting = false;
            }

            await this.returnToLogin();
        },
        async returnToLogin() {
            this.clearTimer();
            await this.$router.replace({ name: "Login" });
        },
        selectVerificationMethod(method) {
            this.verificationMethod = method;
            this.verificationCode = "";
            this.errorKey = null;
        },
        extractRecoveryCodes(data) {
            if (Array.isArray(data)) {
                return data;
            }

            const recoveryCodes = data?.recoveryCodes ?? data?.codes ?? [];
            return Array.isArray(recoveryCodes) ? recoveryCodes : [];
        },
        async copyRecoveryCodes() {
            try {
                await navigator.clipboard.writeText(this.recoveryCodes.join("\n"));
                this.successKey = "com.file.profile.two-factor-codes-copied";
                this.errorKey = null;
            }
            catch {
                this.successKey = null;
                this.errorKey = "com.file.profile.two-factor-copy-error";
            }
        },
        downloadRecoveryCodes() {
            const blob = new Blob([this.recoveryCodes.join("\r\n")], { type: "text/plain;charset=utf-8" });
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = this.$t("com.file.login.two-factor-recovery-codes-file-name");
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
        },
        setChallengeError(error, fallbackKey) {
            const status = error.response?.status;
            if (status === 410 || status === 401) {
                this.errorKey = "com.file.login.two-factor-expired";
                this.remainingSeconds = 0;
                this.clearTimer();
            }
            else if (status === 429) {
                this.errorKey = "com.file.login.two-factor-attempts-exceeded";
                this.remainingSeconds = 0;
                this.clearTimer();
            }
            else {
                this.errorKey = fallbackKey;
            }
        },
        startTimer() {
            this.clearTimer();
            if (this.remainingSeconds <= 0) {
                return;
            }

            this.timer = window.setInterval(() => {
                if (this.remainingSeconds > 0) {
                    this.remainingSeconds -= 1;
                }

                if (this.remainingSeconds <= 0) {
                    this.errorKey = "com.file.login.two-factor-expired";
                    this.clearTimer();
                }
            }, 1000);
        },
        clearTimer() {
            if (this.timer !== null) {
                window.clearInterval(this.timer);
                this.timer = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.two-factor-page {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100vw;
    min-height: 100vh;
    padding: 24px 16px 0;
    overflow-y: auto;
    font-family: Inter;
    background: radial-gradient(235.38% 418.45% at 0% 0%, #00082c 0%, #004dcc 100%);
    box-sizing: border-box;

    &__wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: min(680px, 100%);
    }

    &__logo {
        margin-bottom: 26px;
    }
}

.two-factor-card {
    width: 100%;
    padding: 28px 40px;
    color: #333956;
    background: linear-gradient(146.23deg, #d5e0f5 0%, #ffffff 100%);
    border: 2px solid #ffffff;
    border-radius: 12px;
    box-shadow: 24px 40px 120px rgba(0, 8, 44, 0.32);
    box-sizing: border-box;

    form {
        display: flex;
        flex-direction: column;
    }

    &__header {
        margin: 0;
        color: #00082c;
        font-size: 28px;
        font-weight: 600;
        text-align: center;
    }

    &__text {
        margin: 12px 0 0;
        font-size: 14px;
        text-align: center;

        &--left {
            text-align: left;
        }
    }

    &__expiration {
        margin: 8px 0 16px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
    }

    &__header + &__expiration {
        margin-top: 12px;
    }

    &__label {
        margin: 18px 0 8px;
        color: #00082c;
        font-size: 14px;
        font-weight: 600;
    }

    &__input {
        width: 100%;
        padding: 12px 20px;
        background: #ffffff;
        border: 1px solid #d1d6df;
        border-radius: 4px;
        box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: #00082c;
        }
    }

    &__primary-button,
    &__secondary-button {
        cursor: pointer;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }
    }

    &__primary-button {
        width: 100%;
        margin-top: 24px;
        padding: 14px;
        color: #ffffff;
        font-size: 16px;
        font-weight: 500;
        background: #d80418;
        border: 0;
        border-radius: 4px;
        box-sizing: border-box;

        &:hover:not(:disabled) {
            background: #ad0313;
        }
    }

    &__secondary-button {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin-top: 10px;
        padding: 12px;
        color: #00082c;
        background: transparent;
        border: 1px solid #bbc2ce;
        border-radius: 4px;
        box-sizing: border-box;

        &--inline {
            width: auto;
            flex: 1 1 180px;
            margin-top: 0;
        }
    }

    &__button-icon {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
        object-fit: contain;
    }

    &__code-actions {
        display: flex;
        gap: 8px;
        margin-top: 20px;
    }

    &__method {
        margin-top: 20px;
        overflow: hidden;
        background: #ffffff;
        border: 1px solid #bbc2ce;
        border-radius: 8px;

        &:hover {
            box-shadow: none;
        }
    }

    &__method-tabs {
        display: flex;
        gap: 0;
        padding: 0;
        background: #eef2f8;
        border-bottom: 1px solid #bbc2ce;
    }

    &__method-tab {
        flex: 1 1 0;
        min-width: 0;
        padding: 14px 16px;
        color: #333956;
        font-size: 15px;
        font-weight: 600;
        background: transparent;
        border: 0;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

        & + & {
            border-left: 1px solid #d5dbe5;
        }

        &:hover:not(.two-factor-card__method-tab--active) {
            color: #00082c;
            background: #e2e9f4;
        }

        &:focus-visible {
            outline: 3px solid rgba(0, 77, 204, 0.35);
            outline-offset: -3px;
        }

        &--active {
            color: #00082c;
            background: #ffffff;
            border-bottom-color: #d80418;
        }
    }

    &__method-body {
        min-height: 184px;
        padding: 20px;
    }

    &__method-instruction {
        margin: 0;
        min-height: 2.8em;
        font-size: 14px;
        line-height: 1.4;
    }

    &__method-body &__label {
        display: block;
        margin: 16px 0 8px;
    }

    &__enrollment {
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
        align-items: center;
        margin-top: 18px;
    }

    &__qr {
        width: 220px;
        height: 220px;
        background: #ffffff;
        border: 1px solid #dce4ee;
    }

    &__manual-key {
        display: flex;
        flex: 1 1 260px;
        flex-direction: column;
        min-width: 0;

        code {
            padding: 12px;
            overflow-wrap: anywhere;
            font-size: 16px;
            background: #f4f6f9;
            border-radius: 4px;
        }
    }

    &__notice,
    &__success {
        padding: 12px;
        border-radius: 4px;
    }

    &__notice {
        color: #664d03;
        background: #fff3cd;
        border: 1px solid #ffecb5;
    }

    &__success {
        color: #0f5132;
        background: #d1e7dd;
        border: 1px solid #badbcc;
    }

    &__codes {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 8px;
        margin-top: 16px;
        padding: 16px;
        background: #ffffff;
        border: 1px solid #dce4ee;
        border-radius: 8px;

        code {
            padding: 10px;
            color: #333956;
            text-align: center;
            background: #f4f6f9;
            border: 1px solid #e4e9f1;
            border-radius: 4px;
        }
    }

    &__confirmation {
        display: flex;
        gap: 10px;
        align-items: flex-start;
        margin-top: 20px;
    }
}

@media (max-width: 640px) {
    .two-factor-card {
        padding: 24px 20px;

        &__method-tab {
            padding: 12px 10px;
            font-size: 14px;
        }

        &__method-body {
            padding: 16px;
        }

        &__qr {
            margin: 0 auto;
        }
    }
}
</style>
